// Carrier scope lines, and the ones left with the homeowner (Oct 30, 2026).
//
// The scope-extract edge function reads the carrier's scope and keeps its line
// items in scope_extractions.lines (supabase/functions/scope-extract/map.ts
// ScopeLine). The RM ticks the lines Bardin won't do; the ticked lines go to
// deals.items_not_performing_detail, and trigger deals_apply_scope_picks turns
// them into acv_credit (their ACV), dep_credit (their recoverable depreciation)
// and items_not_performing_lines (their numbers). Migration 20261030_scope_gate.
//
// Greg (Oct 29): credits come only from ticked carrier lines, no typed credits.
//
// ⚠ sameLine() mirrors the stale check in SQL scope_gate_eval, pickTotals() and
// pickedLinesText() mirror the sums in trigger deals_apply_scope_picks. Change
// them together.

import { calcScope } from "./scopeCalc";
import type { Deal } from "./types";

/** One carrier line as the reader stores it (map.ts ScopeLine). */
export interface ScopeLine {
  n: string | null;
  area: string | null;
  coverage: string;
  description: string;
  quantity: number | null;
  unit: string | null;
  rcv: number | null;
  depreciation: number | null;
  depreciation_non_recoverable: boolean;
  acv: number | null;
  category: string;
}

/** A line left with the homeowner, as deals.items_not_performing_detail keeps it. */
export interface PickedLine {
  n: string | null;
  area: string | null;
  coverage: string | null;
  category: string | null;
  description: string;
  quantity: number | null;
  unit: string | null;
  rcv: number | null;
  /** The line's depreciation (recoverable or not; dep_nonrec says which). */
  dep: number;
  /** Non-recoverable depreciation: the carrier never releases it, so it isn't a DEP credit. */
  dep_nonrec: boolean;
  acv: number;
  /** The scope_extractions row the line was picked from. */
  read_id: string | null;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const cents = (n: number) => Math.round(n * 100);

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function text(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
}

/** scope_extractions.lines, read defensively (an old or odd row never throws). */
export function readScopeLines(v: unknown): ScopeLine[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((l): l is Record<string, unknown> => !!l && typeof l === "object")
    .map((l) => ({
      n: text(l.n),
      area: text(l.area),
      coverage: String(l.coverage ?? ""),
      description: String(l.description ?? "").trim(),
      quantity: num(l.quantity),
      unit: text(l.unit),
      rcv: num(l.rcv),
      depreciation: num(l.depreciation),
      depreciation_non_recoverable: l.depreciation_non_recoverable === true,
      acv: num(l.acv),
      category: String(l.category ?? ""),
    }));
}

/** deals.items_not_performing_detail, read defensively. */
export function readPicks(v: unknown): PickedLine[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((p): p is Record<string, unknown> => !!p && typeof p === "object")
    .map((p) => {
      const rcv = num(p.rcv);
      const dep = num(p.dep) ?? 0;
      return {
        n: text(p.n),
        area: text(p.area),
        coverage: text(p.coverage),
        category: text(p.category),
        description: String(p.description ?? "").trim(),
        quantity: num(p.quantity),
        unit: text(p.unit),
        rcv,
        dep,
        dep_nonrec: p.dep_nonrec === true,
        // The trigger's fallback when a pick carries no ACV.
        acv: num(p.acv) ?? round2((rcv ?? 0) - dep),
        read_id: text(p.read_id),
      };
    });
}

/** A line's ACV: as printed, or RCV − depreciation when the carrier prints none (State Farm). */
export function lineAcv(line: Pick<ScopeLine, "acv" | "rcv" | "depreciation">): number {
  return line.acv ?? round2((line.rcv ?? 0) - (line.depreciation ?? 0));
}

/** A line's depreciation ($0 when none is printed). */
export function lineDep(line: Pick<ScopeLine, "depreciation">): number {
  return line.depreciation ?? 0;
}

export function toPick(line: ScopeLine, readId: string | null): PickedLine {
  return {
    n: line.n,
    area: line.area,
    coverage: line.coverage || null,
    category: line.category || null,
    description: line.description.trim(),
    quantity: line.quantity,
    unit: line.unit,
    rcv: line.rcv,
    dep: round2(lineDep(line)),
    dep_nonrec: line.depreciation_non_recoverable === true,
    acv: round2(lineAcv(line)),
    read_id: readId,
  };
}

const nKey = (v: string | null | undefined) => (v ?? "").trim();
// SQL compares numerics exactly (<= 0.01); a float difference of 0.0100000001 must still count.
const TOLERANCE = 0.01 + 1e-6;

/** ⚠ Mirror of scope_gate_eval's stale check: same number, same description, ACV and depreciation within $0.01. */
export function sameLine(line: ScopeLine, pick: Pick<PickedLine, "n" | "description" | "acv" | "dep">): boolean {
  return (
    nKey(line.n) === nKey(pick.n) &&
    line.description.trim() === (pick.description ?? "").trim() &&
    Math.abs(lineAcv(line) - (pick.acv ?? 0)) <= TOLERANCE &&
    Math.abs(lineDep(line) - (pick.dep ?? 0)) <= TOLERANCE
  );
}

/** Which of the read's lines the saved picks are (by index), and the picks no line of the read matches any more. */
export function matchPicks(lines: ScopeLine[], picks: PickedLine[]): { selected: Set<number>; stale: PickedLine[] } {
  const selected = new Set<number>();
  const stale: PickedLine[] = [];
  for (const p of picks) {
    const i = lines.findIndex((l, idx) => !selected.has(idx) && sameLine(l, p));
    if (i >= 0) selected.add(i);
    else stale.push(p);
  }
  return { selected, stale };
}

/** ⚠ Mirror of trigger deals_apply_scope_picks: ACV credit = the picks' ACV; DEP credit = their recoverable depreciation. */
export function pickTotals(picks: PickedLine[]): { acv: number; dep: number; count: number } {
  let acv = 0;
  let dep = 0;
  for (const p of picks) {
    acv += p.acv;
    if (!p.dep_nonrec) dep += p.dep;
  }
  return { acv: round2(acv), dep: round2(dep), count: picks.length };
}

/** ⚠ Mirror of the trigger's items_not_performing_lines: the line numbers, or the first 40 characters when a line has none. */
export function pickedLinesText(picks: PickedLine[]): string | null {
  if (picks.length === 0) return null;
  return picks.map((p) => nKey(p.n) || p.description.trim().slice(0, 40)).join(", ");
}

/** A short signature of the picks (agreement staleness): number · description · ACV · DEP. */
export function picksSignature(picks: PickedLine[]): string {
  return picks
    .map((p) => `${nKey(p.n)}·${p.description.trim()}·${cents(p.acv)}·${cents(p.dep)}·${p.dep_nonrec ? 1 : 0}`)
    .join("|");
}

// ── Groups ──────────────────────────────────────────────────────────────────

export type LineGroupKey = "interior" | "gutters" | "trades" | "roof";

interface GroupMeta {
  key: LineGroupKey;
  /** Staff screens (the picker, Alex and Chad, the crew). */
  title: string;
  /** What the homeowner reads on the agreement. */
  homeownerTitle: string;
  hint: string;
  /** Roof is Bardin's work: the picker folds it. */
  folded: boolean;
}

export const LINE_GROUPS: readonly GroupMeta[] = [
  { key: "interior", title: "Interior", homeownerTitle: "Inside your home", hint: "Usually left with the homeowner", folded: false },
  { key: "gutters", title: "Gutters and downspouts", homeownerTitle: "Gutters and downspouts", hint: "Left with the homeowner on about half of deals", folded: false },
  { key: "trades", title: "Other trades", homeownerTitle: "Other work", hint: "Siding, paint, windows, HVAC and the rest", folded: false },
  { key: "roof", title: "Roof and general", homeownerTitle: "Roof and general work", hint: "Bardin's work. Leave a roof line only if the crew truly won't do it.", folded: true },
];

/** The reader's category → the picker's group. General lines (debris haul, permits, cleaning) go with the roof. */
export function lineGroupKey(category: string | null | undefined): LineGroupKey {
  switch ((category ?? "").toLowerCase()) {
    case "interior":
      return "interior";
    case "gutters":
      return "gutters";
    case "roof":
    case "general":
      return "roof";
    default:
      return "trades";
  }
}

export interface LineGroup extends GroupMeta {
  items: Array<{ line: ScopeLine; index: number }>;
}

/** The read's lines in the picker's order: Interior, Gutters, other trades, Roof last. Only the main coverage's
 *  lines are in the deal's numbers (code upgrade, other structures, contents aren't), so the rest are counted. */
export function groupLines(lines: ScopeLine[], mainCoverage: string | null | undefined): { groups: LineGroup[]; otherCoverage: number } {
  const groups: LineGroup[] = LINE_GROUPS.map((g) => ({ ...g, items: [] }));
  let otherCoverage = 0;
  lines.forEach((line, index) => {
    if (mainCoverage && line.coverage !== mainCoverage) {
      otherCoverage += 1;
      return;
    }
    const g = groups.find((x) => x.key === lineGroupKey(line.category));
    g?.items.push({ line, index });
  });
  return { groups: groups.filter((g) => g.items.length > 0), otherCoverage };
}

export interface PickGroup extends GroupMeta {
  items: PickedLine[];
  acv: number;
}

/** The picks grouped the same way, for the agreement, Alex and Chad, and the crew. */
export function groupPicks(picks: PickedLine[]): PickGroup[] {
  return LINE_GROUPS.map((g) => {
    const items = picks.filter((p) => lineGroupKey(p.category) === g.key);
    return { ...g, items, acv: round2(items.reduce((t, p) => t + p.acv, 0)) };
  }).filter((g) => g.items.length > 0);
}

/** "Bedroom · 120 SF" — where the line is and how much of it. */
export function lineMeta(l: { area: string | null; quantity: number | null; unit: string | null }): string {
  const qty = l.quantity == null ? null : `${Number.isInteger(l.quantity) ? l.quantity : l.quantity.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")}${l.unit ? ` ${l.unit}` : ""}`;
  return [l.area, qty].filter(Boolean).join(" · ");
}

// ── The homeowner's share with the picks ────────────────────────────────────

export interface PickedShare {
  deductible: number;
  /** Upgrades, additional work and at-cost extras the homeowner pays on top of the deductible. */
  addOns: number;
  /** The picks' ACV. */
  acv: number;
  /** The part of it that comes off the deductible (credits stop at the deductible). */
  acvApplied: number;
  /** Other credits on the deal (misc credit) that come off the deductible. */
  otherApplied: number;
  /** Credit beyond the deductible, which nobody collects. */
  overage: number;
  /** The picks' recoverable depreciation: the carrier keeps it until the work is done. */
  dep: number;
  /** What the homeowner pays Bardin before the build (calcScope hoOutOfPocket). */
  homeownerPays: number;
}

/** calcScope with the picks as the deal's credits. ACV counts first against the deductible, then other credits. */
export function shareWithPicks(deal: Partial<Deal>, picks: PickedLine[]): PickedShare {
  const t = pickTotals(picks);
  const calc = calcScope({ ...deal, acv_credit: t.acv, dep_credit: t.dep });
  const acvApplied = round2(Math.min(t.acv, calc.deductible));
  return {
    deductible: calc.deductible,
    addOns: round2(calc.hoGross - calc.deductible),
    acv: t.acv,
    acvApplied,
    otherApplied: round2(Math.max(0, calc.creditsApplied - acvApplied)),
    overage: round2(calc.creditOverage),
    dep: t.dep,
    homeownerPays: round2(calc.hoOutOfPocket),
  };
}
